/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, IconButton, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { faCheck, faTimes, faExclamationTriangle, faUndo, faSpellCheck, faClipboardCheck, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gql, useMutation } from '@apollo/client';
import Lottie from 'lottie-react';
import { useTranslation } from 'react-i18next';
import AnimationFile from '../../assets/lotties/AI loading.json';
import c from '../../constant';
import { locateSuggestionRange, wrapRangeInSpan, SPELL_FLAG_ACTIVE_CLASS, SPELL_FLAG_CSS } from '../../utils/spell-highlight';
import useStore from "../../store";

// Dedicated to this panel — DocxViewer keeps calling checkSpelling, so the two
// features can be tuned without affecting each other.
const REVIEW_DOCUMENT = gql`
	mutation ReviewDocument($text: String!, $houseStyle: String, $includeSuggestions: Boolean, $includeNotes: Boolean) {
		reviewDocument(text: $text, houseStyle: $houseStyle, includeSuggestions: $includeSuggestions, includeNotes: $includeNotes) {
			suggestions {
				original
				suggestion
				context
			}
			notes {
				note
			}
		}
	}
`;

// Same review, plus the text of the record's attachment files. Attachment text
// comes from the server's parsed-file cache, so each file is read by the AI at
// most once ever — a repeat review of the same attachments costs nothing extra.
const REVIEW_WITH_ATTACHMENTS = gql`
	mutation ReviewDocumentWithAttachments($text: String!, $files: [AttachmentFileInput!], $houseStyle: String) {
		reviewDocumentWithAttachments(text: $text, files: $files, houseStyle: $houseStyle) {
			suggestions {
				original
				suggestion
				context
			}
			notes {
				note
			}
		}
	}
`;

const REJECT_SPELLING_SUGGESTION = gql`
	mutation RejectSpellingSuggestion($original: String!, $suggestion: String!) {
		rejectSpellingSuggestion(original: $original, suggestion: $suggestion)
	}
`;

// Spelling + advisory-notes review inside the AI side panel.
//
// Unlike DocxViewer's version, corrections are not applied to the document as
// each one is accepted: this reviews a saved database record, so accepting
// only stages a decision and a single Save at the end writes them all through
// one mutation. That keeps a half-finished review from leaving the record in a
// partially-corrected state.
//
// `onSave` receives the accepted corrections and owns the actual persistence,
// so this component stays reusable for any record type.
// `houseStyle` is an optional description of the writing conventions this
// record is expected to follow. It only ever influences the advisory notes —
// never the spelling suggestions — and is advisory by design, since
// departments legitimately write in different styles.
// `attachmentFiles` enables the "Overall + attachment" action. Each entry is
// `{ url, label }`, where the label names the file the way a reviewer sees it
// (section + row text) so findings can cite it.
// `canSave` mirrors the editor's own edit permission: corrections are written
// through the same mutation, so a user who cannot edit the record at its
// current workflow step must not be offered a Save here either. Reviewing is
// still allowed — only writing back is gated.
function SpellCheckSection({ text, onSave, saving = false, contentRef = null, houseStyle = null, attachmentFiles = [], canSave = true }) {
	const { t } = useTranslation();
	// Each: { id, original, suggestion, context, status: 'pending'|'accepted'|'rejected' }
	const [suggestions, setSuggestions] = useState(null);
	const [notes, setNotes] = useState([]);
	const [hasRun, setHasRun] = useState(false);
	const [hoveredId, setHoveredId] = useState(null);
	// Which of the three actions produced the results on screen: 'spelling'
	// (typos only), 'sketchy' (document-level notes only) or 'overall'
	// (both, plus the house-style conventions).
	const [mode, setMode] = useState(null);

	const [rejectSpellingSuggestion] = useMutation(REJECT_SPELLING_SUGGESTION);

	const { currentUser } = useStore((state) => state.currentUser);

	// Both mutations return the same shape, so they share one result handler.
	const applyResult = (result) => {
		const value = result || { suggestions: [], notes: [] };
		setSuggestions((value.suggestions || []).map((s, i) => ({ ...s, id: `spell-${i}`, status: 'pending' })));
		setNotes(value.notes || []);
	};

	const [reviewDocument, { loading: checkingDocument, error: documentError }] = useMutation(REVIEW_DOCUMENT, {
		onCompleted: (data) => applyResult(data?.reviewDocument),
	});

	const [reviewWithAttachments, { loading: checkingAttachments, error: attachmentError }] = useMutation(REVIEW_WITH_ATTACHMENTS, {
		onCompleted: (data) => applyResult(data?.reviewDocumentWithAttachments),
	});

	const checking = checkingDocument || checkingAttachments;
	const error = documentError || attachmentError;

	// A different record means the previous review no longer applies.
	useEffect(() => {
		setSuggestions(null);
		setNotes([]);
		setHasRun(false);
		setHoveredId(null);
		setMode(null);
	}, [text]);

	// Marks each flagged phrase in the rendered note with a <span data-spell-id>
	// so it can be scrolled to and highlighted on hover. Jodit renders its
	// viewers asynchronously, so this retries briefly rather than giving up if
	// the content isn't in the DOM the instant results arrive.
	useEffect(() => {
		if (!contentRef?.current || !suggestions?.length) return undefined;

		let attempts = 0;
		let timer = null;

		const mark = () => {
			const container = contentRef.current;
			if (!container) return;
			let markedAny = false;
			suggestions.forEach((s) => {
				if (container.querySelector(`[data-spell-id="${s.id}"]`)) {
					markedAny = true;
					return;
				}
				try {
					const range = locateSuggestionRange(container, s.original, s.context);
					if (range) {
						wrapRangeInSpan(range, s.id);
						markedAny = true;
					}
				} catch (err) {
					// One phrase failing to line up with the live DOM must not stop
					// the rest from marking.
					console.warn('SpellCheckSection: failed to mark suggestion', s.id, err);
				}
			});
			// Jodit mounts its editors in a later frame; retry a few times before
			// accepting that some phrases simply aren't findable in the DOM.
			if (!markedAny && attempts < 10) {
				attempts += 1;
				timer = setTimeout(mark, 300);
			}
		};

		mark();
		return () => {
			if (timer) clearTimeout(timer);
		};
		// Keyed on the *number* of suggestions (i.e. a fresh result set) rather
		// than the array identity: accept/reject replaces the array on every
		// click, and re-running then would re-walk text that is already marked.
	}, [suggestions?.length, contentRef]);

	// Scrolls the hovered suggestion into view and highlights it. Imperative
	// because the marker spans live inside Jodit-rendered content React doesn't
	// control, so they can't be driven by a className prop.
	useEffect(() => {
		const container = contentRef?.current;
		if (!container) return;
		const active = container.querySelector(`.${SPELL_FLAG_ACTIVE_CLASS}`);
		if (active) active.classList.remove(SPELL_FLAG_ACTIVE_CLASS);
		if (!hoveredId) return;
		const el = container.querySelector(`[data-spell-id="${hoveredId}"]`);
		if (el) {
			el.classList.add(SPELL_FLAG_ACTIVE_CLASS);
			el.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, [hoveredId, contentRef]);

	// House-style conventions are sent only in 'overall'. Keeping them out of
	// 'sketchy' makes the two directly comparable — same document, same notes
	// pass, with and without the conventions — which is what makes it possible
	// to judge whether they actually help.
	function runCheck(nextMode) {
		if (!text?.trim()) return;
		setMode(nextMode);
		setHasRun(true);
		setSuggestions(null);
		setNotes([]);

		if (nextMode === 'attachments') {
			reviewWithAttachments({
				variables: {
					// Labels travel with the URLs so the AI can name the offending
					// document rather than pointing at "Reference 1".
					files: attachmentFiles.map((file) => ({ url: file.url, label: file.label || null })),
					text,
					houseStyle: houseStyle || null,
				},
			});
			return;
		}

		reviewDocument({
			variables: {
				text,
				houseStyle: nextMode === 'overall' ? houseStyle || null : null,
				includeSuggestions: nextMode !== 'sketchy',
				includeNotes: nextMode !== 'spelling',
			},
		});
	}

	function setStatus(id, status) {
		setSuggestions((current) => current.map((s) => (s.id === id ? { ...s, status } : s)));
	}

	function reject(suggestion) {
		setStatus(suggestion.id, 'rejected');
		// Feeds the API's shared rejection counter so a correction the office
		// keeps dismissing stops being offered on future checks.
		rejectSpellingSuggestion({
			variables: { original: suggestion.original, suggestion: suggestion.suggestion },
		}).catch(() => null);
	}

	const accepted = (suggestions || []).filter((s) => s.status === 'accepted');
	const pending = (suggestions || []).filter((s) => s.status === 'pending');
	const decided = (suggestions || []).filter((s) => s.status !== 'pending');

	// The three actions stay visible after a run so a reviewer can switch
	// between them (notably: 'sketchy' then 'overall', to see what the
	// house-style conventions add) without reloading the page.
	//
	// Stacked one per row rather than side by side: each mode costs a different
	// number of model calls, and a full-width row leaves space to say what it
	// actually does — which is the difference between an informed choice and
	// three unlabelled words.
	const MODE_ACTIONS = [
		{ key: 'spelling', label: 'Spelling', description: 'Typos and misspellings only', icon: faSpellCheck },
		{ key: 'sketchy', label: 'Sketchy', description: 'Things that look off in the document', icon: faExclamationTriangle },
		{ key: 'overall', label: 'Overall', description: 'Spelling, sketchy, and house style', icon: faClipboardCheck },
	];

	// Only offered when the record actually has files — with none this would be
	// a slower, identical 'overall'.
	//
	// Note: this checks the NOTE against its attachments; the attachments are
	// evidence, not the thing being reviewed. Nothing inside them is flagged.
	if (attachmentFiles?.length && currentUser?.position < 4) {
		MODE_ACTIONS.push({
			key: 'attachments',
			label: 'Overall + attachment',
			description: 'Checks the note against its attached files (scans may misread)',
			icon: faPaperclip,
		});
	}

	const modeButtons = (
		<Flex direction="column" gap="8px" mt="4px" mb="16px">
			{MODE_ACTIONS.map((action) => {
				const isActive = mode === action.key;
				const isDisabled = !text?.trim() || checking;
				return (
					<Flex
						key={action.key}
						as="button"
						type="button"
						aria-pressed={isActive}
						disabled={isDisabled}
						onClick={() => !isDisabled && runCheck(action.key)}
						align="center"
						gap="10px"
						w="100%"
						px="12px"
						py="11px"
						textAlign="left"
						borderWidth="1px"
						borderColor={isActive ? 'green.400' : 'gray.200'}
						bg={isActive ? 'green.50' : 'white'}
						borderRadius={c.borderRadius}
						cursor={isDisabled ? 'not-allowed' : 'pointer'}
						opacity={isDisabled && !isActive ? 0.5 : 1}
						transition="background 0.12s ease-out, border-color 0.12s ease-out"
						_hover={isDisabled ? {} : { bg: isActive ? 'green.50' : 'gray.50', borderColor: isActive ? 'green.400' : 'gray.300' }}
						mb="1"
					>
						<Flex
							align="center"
							justify="center"
							flexShrink={0}
							w="26px"
							h="26px"
							borderRadius="full"
							bg={isActive ? 'green.500' : 'gray.100'}
							color={isActive ? 'white' : 'gray.500'}
						>
							{checking && isActive ? (
								<Spinner size="xs" />
							) : (
								<FontAwesomeIcon icon={action.icon} style={{ fontSize: 12 }} />
							)}
						</Flex>
						<Box minW={0} flex="1" pl="2">
							<Text fontSize="13px" fontWeight="600" color={isActive ? 'green.800' : 'gray.800'} lineHeight="1.4">
								{t(action.label)}
							</Text>
						</Box>
					</Flex>
				);
			})}
		</Flex>
	);

	if (!hasRun) {
		return <Box>{modeButtons}</Box>;
	}

	return (
		<Box>
			{modeButtons}
			{/* Styles the marker spans injected into the note content, which lives
			    outside this component's tree. */}
			<style>{SPELL_FLAG_CSS}</style>
			{checking && (
				<Flex direction="column" align="center" justify="center" py="24px" gap="8px">
					<Box w="80px">
						<Lottie loop animationData={AnimationFile} />
					</Box>
					<Text fontSize="13px" color="gray.500">
						{mode === 'spelling' ? t('Checking spelling...') : t('Reviewing document...')}
					</Text>
				</Flex>
			)}

			{!checking && error && (
				<Box p="10px" bg="red.50" borderRadius={c.borderRadius} mb="8px">
					<Text fontSize="12px" color="red.700">
						{t('Spelling check failed')}
					</Text>
					<Button size="xs" mt="6px" onClick={() => runCheck(mode)}>
						{t('Try again')}
					</Button>
				</Box>
			)}

			{!checking && !error && suggestions && (
				<>
					{/* The spelling summary is meaningless in 'sketchy' mode, which
					    never requests suggestions — showing "0 to review" there
					    would read as "no typos" when none were looked for. */}
					{mode !== 'sketchy' && (
						<Flex align="center" justify="space-between" mb="8px">
							<Text fontSize="12px" color="gray.600">
								{pending.length} {t('to review')}
								{decided.length > 0 ? ` · ${accepted.length} ${t('accepted')}` : ''}
							</Text>
							<Button size="xs" variant="ghost" onClick={() => runCheck(mode)}>
								{t('Re-run')}
							</Button>
						</Flex>
					)}

					{mode !== 'sketchy' && suggestions.length === 0 && (
						<Text fontSize="13px" color="gray.500" mb="8px">
							{t('No spelling issues found')}
						</Text>
					)}

					{suggestions.map((s) => (
						<Box
							key={s.id}
							p="8px"
							mb="6px"
							borderWidth="1px"
							borderColor={s.status === 'accepted' ? 'green.200' : s.status === 'rejected' ? 'gray.200' : 'gray.200'}
							bg={s.status === 'accepted' ? 'green.50' : s.status === 'rejected' ? 'gray.50' : 'white'}
							borderRadius={c.borderRadius}
							opacity={s.status === 'rejected' ? 0.6 : 1}
							cursor={contentRef ? 'pointer' : 'default'}
							onMouseEnter={() => setHoveredId(s.id)}
							onMouseLeave={() => setHoveredId(null)}
						>
							<Text fontSize="13px">
								<Text as="span" color="red.500" textDecoration="line-through">
									{s.original}
								</Text>
								{' → '}
								<Text as="span" color="green.600" fontWeight="medium">
									{s.suggestion}
								</Text>
							</Text>
							{s.context && (
								<Text fontSize="11px" color="gray.500" mt="2px" noOfLines={2}>
									{s.context}
								</Text>
							)}
							<Flex gap="6px" mt="6px" align="center">
								{s.status === 'pending' ? (
									<>
										<Tooltip label={t('Accept')}>
											<IconButton
												aria-label="accept-suggestion"
												icon={<FontAwesomeIcon icon={faCheck} />}
												size="xs"
												colorScheme="green"
												onClick={() => setStatus(s.id, 'accepted')}
											/>
										</Tooltip>
										<Tooltip label={t('Reject')}>
											<IconButton
												aria-label="reject-suggestion"
												icon={<FontAwesomeIcon icon={faTimes} />}
												size="xs"
												colorScheme="red"
												variant="outline"
												onClick={() => reject(s)}
											/>
										</Tooltip>
									</>
								) : (
									<Flex align="center" gap="6px">
										<Text fontSize="11px" color={s.status === 'accepted' ? 'green.700' : 'gray.500'}>
											{s.status === 'accepted' ? t('Accepted') : t('Rejected')}
										</Text>
										<Tooltip label={t('Undo')}>
											<IconButton
												aria-label="undo-suggestion"
												icon={<FontAwesomeIcon icon={faUndo} />}
												size="xs"
												variant="ghost"
												onClick={() => setStatus(s.id, 'pending')}
											/>
										</Tooltip>
									</Flex>
								)}
							</Flex>
						</Box>
					))}

					{mode !== 'spelling' && notes.length === 0 && (
						<Text fontSize="13px" color="gray.500" mb="8px">
							{t('Nothing looks off in this document')}
						</Text>
					)}

					{notes.length > 0 && (
						<Box p="8px" mt="10px" bg="orange.50" borderRadius={c.borderRadius}>
							<Flex align="center" gap="6px" mb="6px">
								<FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 12, color: '#c05621' }} />
								<Text fontSize="11px" fontWeight="semibold" color="orange.800">
									{t('Note: possible issues beyond spelling')}
								</Text>
							</Flex>
							{notes.map((n, i) => (
								<Text key={i} fontSize="12px" color="orange.900" mb={i < notes.length - 1 ? '4px' : 0}>
									• {n.note}
								</Text>
							))}
						</Box>
					)}

					{/* Without edit permission the review is still useful to read, so
					    the suggestions stay — only the write-back is withheld, with
					    the reason stated rather than a silently dead button. */}
					{suggestions.length > 0 && !canSave && (
						<Box p="8px" mt="10px" bg="gray.50" borderRadius={c.borderRadius}>
							<Text fontSize="11px" color="gray.600">
								{t('You cannot edit this document at this step, so corrections cannot be saved.')}
							</Text>
						</Box>
					)}

					{suggestions.length > 0 && canSave && (
						<Box position="sticky" bottom="0" bg="white" pt="8px" mt="4px" borderTop="1px solid #ebebeb">
							<Button
								size="sm"
								w="100%"
								colorScheme="green"
								borderRadius={c.borderRadius}
								isLoading={saving}
								loadingText={t('Saving')}
								isDisabled={accepted.length === 0 || pending.length > 0}
								onClick={() => onSave(accepted)}
							>
								{t('Save')}
								{accepted.length > 0 ? ` (${accepted.length})` : ''}
							</Button>
							{pending.length > 0 && (
								<Text fontSize="11px" color="gray.500" mt="4px" textAlign="center">
									{t('Accept or reject every suggestion before saving.')}
								</Text>
							)}
						</Box>
					)}
				</>
			)}
		</Box>
	);
}

export default SpellCheckSection;
